import { useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { renderStorefrontIcon } from "./storefrontIcons";
import { t } from "../../i18n";

function getCaseStudies() {
  return [
    {
      title: t("oemCase1Title"),
      imageUrl: "/assets/oem/case-studio-starter.jpg",
      summary: t("oemCase1Summary"),
      details: t("oemCase1Details")
    },
    {
      title: t("oemCase2Title"),
      imageUrl: "/assets/oem/case-retail-refresh.jpg",
      summary: t("oemCase2Summary"),
      details: t("oemCase2Details")
    },
    {
      title: t("oemCase3Title"),
      imageUrl: "/assets/oem/case-marketplace-bundle.jpg",
      summary: t("oemCase3Summary"),
      details: t("oemCase3Details")
    }
  ];
}

function getOemServiceCards() {
  return [
    {
      title: t("oemProcess1Title"),
      body: t("oemProcess1Body"),
      imageUrl: "/assets/oem/oem-tag-cut.jpg",
      iconKey: "tag-cut"
    },
    {
      title: t("oemProcess2Title"),
      body: t("oemProcess2Body"),
      imageUrl: "/assets/oem/oem-layers.jpg",
      iconKey: "layers"
    },
    {
      title: t("oemProcess3Title"),
      body: t("oemProcess3Body"),
      imageUrl: "/assets/oem/oem-settings.jpg",
      iconKey: "settings"
    },
    {
      title: t("oemProcess4Title"),
      body: t("oemProcess4Body"),
      imageUrl: "/assets/oem/oem-quality.jpg",
      iconKey: "shield-check"
    }
  ];
}

function getCustomizationComparisons() {
  return [
    {
      title: t("oemCompare1Title"),
      body: t("oemCompare1Body"),
      beforeLabel: t("oemCompare1BeforeLabel"),
      afterLabel: t("oemCompare1AfterLabel"),
      customizationLabel: t("oemCompare1CustomizationLabel"),
      customizationItems: [t("oemCompare1Item1"), t("oemCompare1Item2"), t("oemCompare1Item3")],
      beforeImageUrl: "/assets/oem/compare-logo-before.svg",
      afterImageUrl: "/assets/oem/compare-logo-after.svg"
    },
    {
      title: t("oemCompare2Title"),
      body: t("oemCompare2Body"),
      beforeLabel: t("oemCompare2BeforeLabel"),
      afterLabel: t("oemCompare2AfterLabel"),
      customizationLabel: t("oemCompare2CustomizationLabel"),
      customizationItems: [t("oemCompare2Item1"), t("oemCompare2Item2"), t("oemCompare2Item3")],
      beforeImageUrl: "/assets/oem/compare-color-before.svg",
      afterImageUrl: "/assets/oem/compare-color-after.svg"
    },
    {
      title: t("oemCompare3Title"),
      body: t("oemCompare3Body"),
      beforeLabel: t("oemCompare3BeforeLabel"),
      afterLabel: t("oemCompare3AfterLabel"),
      customizationLabel: t("oemCompare3CustomizationLabel"),
      customizationItems: [t("oemCompare3Item1"), t("oemCompare3Item2"), t("oemCompare3Item3")],
      beforeImageUrl: "/assets/oem/compare-pack-before.svg",
      afterImageUrl: "/assets/oem/compare-pack-after.svg"
    },
    {
      title: t("oemCompare4Title"),
      body: t("oemCompare4Body"),
      beforeLabel: t("oemCompare4BeforeLabel"),
      afterLabel: t("oemCompare4AfterLabel"),
      customizationLabel: t("oemCompare4CustomizationLabel"),
      customizationItems: [t("oemCompare4Item1"), t("oemCompare4Item2"), t("oemCompare4Item3")],
      beforeImageUrl: "/assets/oem/compare-bundle-before.svg",
      afterImageUrl: "/assets/oem/compare-bundle-after.svg"
    }
  ];
}

function getContactFlow() {
  return [
    t("oemContactFlow1"),
    t("oemContactFlow2"),
    t("oemContactFlow3"),
    t("oemContactFlow4"),
    t("oemContactFlow5")
  ];
}

export default function OemPrivateLabelPage({ apiBaseUrl }) {
  const [currency, setCurrency] = useState("USD");
  const [showCustomizationImage, setShowCustomizationImage] = useState(true);
  const CASE_STUDIES = getCaseStudies();
  const OEM_SERVICE_CARDS = getOemServiceCards();
  const CUSTOMIZATION_COMPARISONS = getCustomizationComparisons();
  const CONTACT_FLOW = getContactFlow();

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
    >
      <section className="subpage-hero oem-hero">
        <p className="hero-kicker">{t("oemHeroKicker")}</p>
        <h2>{t("oemTitle")}</h2>
        <p>{t("oemIntro")}</p>
        <div className="oem-hero-actions">
          <Link className="button" to="/contact?topic=oem">{t("oemStartProject")}</Link>
          <Link className="button ghost-button" to="/products">{t("oemBrowseProducts")}</Link>
        </div>
      </section>

      <section className="section-title">
        <h2>{t("oemVisualGuide")}</h2>
      </section>
      <section className="oem-visual-panel">
        {showCustomizationImage ? (
          <img
            className="oem-visual-image"
            src="/assets/about/oem-customization-guide.jpg"
            alt={t("oemVisualAlt")}
            loading="lazy"
            onError={() => setShowCustomizationImage(false)}
          />
        ) : (
          <div className="oem-visual-placeholder">
            <p>{t("oemVisualPlaceholder")}</p>
          </div>
        )}
      </section>

      <section className="oem-service-showcase" aria-label={t("oemServiceShowcaseAria")}>
        <div className="oem-service-showcase-head">
          <h2>{t("oemServiceShowcaseTitle")}</h2>
          <p>
            {t("oemServiceShowcaseBody")}
          </p>
        </div>

        <div className="oem-service-showcase-grid">
          {OEM_SERVICE_CARDS.map((item, index) => (
            <article key={`oem-service-${item.title}`} className="oem-service-showcase-card">
              <div className="oem-service-showcase-media">
                <img src={item.imageUrl} alt={item.title} loading="lazy" />
                <span className="oem-service-showcase-index">{String(index + 1).padStart(2, "0")}</span>
              </div>
              <div className="oem-service-showcase-copy">
                <span className="oem-service-showcase-icon" aria-hidden="true">
                  {renderStorefrontIcon(item.iconKey)}
                </span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="oem-capability-compare-section">
        <div className="oem-capability-compare-head">
          <div>
            <p className="hero-kicker">{t("oemCapabilityKicker")}</p>
            <h3>{t("oemCapabilityTitle")}</h3>
          </div>
          <p>{t("oemCompareHeadBody")}</p>
        </div>

        <div className="oem-capability-compare-stack">
          {CUSTOMIZATION_COMPARISONS.map((item, index) => (
            <article key={item.title} className="oem-capability-compare-card">
              <div className="oem-capability-compare-copy">
                <span className="oem-capability-compare-index">{String(index + 1).padStart(2, "0")}</span>
                <h4>{item.title}</h4>
                <p>{item.body}</p>
              </div>

              <div className="oem-capability-compare-visuals">
                <figure className="oem-capability-compare-figure">
                  <img src={item.beforeImageUrl} alt={`${item.title} ${t("oemCompareBaseSuffix")}`} loading="lazy" />
                  <figcaption>{item.beforeLabel}</figcaption>
                </figure>

                <div className="oem-capability-compare-customization" aria-label={`${item.title} ${t("oemCompareOptionsSuffix")}`}>
                  <span className="oem-capability-compare-customization-label">{item.customizationLabel}</span>
                  <div className="oem-capability-compare-customization-items">
                    {item.customizationItems.map((entry) => (
                      <span key={`${item.title}-${entry}`}>{entry}</span>
                    ))}
                  </div>
                </div>

                <figure className="oem-capability-compare-figure is-after">
                  <img src={item.afterImageUrl} alt={`${item.title} ${t("oemCompareCustomizedSuffix")}`} loading="lazy" />
                  <figcaption>{item.afterLabel}</figcaption>
                </figure>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section-title">
        <h2>{t("oemCasesTitle")}</h2>
      </section>
      <section className="oem-case-grid">
        {CASE_STUDIES.map((item) => (
          <article key={item.title} className="oem-case-card">
            <img className="oem-case-image" src={item.imageUrl} alt={item.title} loading="lazy" />
            <h3>{item.title}</h3>
            <p>{item.summary}</p>
            <small>{item.details}</small>
          </article>
        ))}
      </section>

      <section className="about-layout oem-contact-layout">
        <article className="about-main">
          <p className="hero-kicker">{t("oemOrderingFlowKicker")}</p>
          <h3>{t("oemOrderingFlowTitle")}</h3>
          <ol className="oem-flow-list">
            {CONTACT_FLOW.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </article>

        <aside className="about-side oem-contact-side">
          <h3>{t("oemContactSideTitle")}</h3>
          <p>
            {t("oemContactSideBody")}
          </p>
          <div className="contact-note">
            {t("oemContactSideNote")}
          </div>
          <Link className="button oem-side-cta" to="/contact?topic=oem">{t("oemContactSideCta")}</Link>
        </aside>
      </section>
    </StorefrontLayout>
  );
}